import { signal } from '@preact/signals';

/** Text-to-speech via speechSynthesis (works in installed iOS PWAs).
 *  Drives the voice button's "speaking" state. */

export const speaking = signal(false);
export const ttsMuted = signal(localStorage.getItem('tend.tts.muted') === '1');

let voice: SpeechSynthesisVoice | null = null;
let utteranceQueue = 0;

function pickVoice(): SpeechSynthesisVoice | null {
  const wanted = localStorage.getItem('tend.tts.voice');
  const voices = speechSynthesis.getVoices();
  if (!voices.length) return null;
  if (wanted) {
    const v = voices.find((v) => v.name === wanted);
    if (v) return v;
  }
  const lang = navigator.language || 'en-US';
  return (
    voices.find((v) => v.lang === lang && v.localService) ??
    voices.find((v) => v.lang.startsWith('en') && v.localService) ??
    voices.find((v) => v.lang.startsWith('en')) ??
    voices[0]
  );
}

if ('speechSynthesis' in window) {
  speechSynthesis.addEventListener('voiceschanged', () => {
    voice = pickVoice();
  });
  voice = pickVoice();
}

export function setTtsMuted(m: boolean) {
  ttsMuted.value = m;
  localStorage.setItem('tend.tts.muted', m ? '1' : '0');
  if (m) stopSpeaking();
}

export function setTtsVoice(name: string) {
  localStorage.setItem('tend.tts.voice', name);
  voice = pickVoice();
}

export function listVoices(): SpeechSynthesisVoice[] {
  return speechSynthesis.getVoices().filter((v) => v.lang.startsWith('en'));
}

export function speakText(text: string, onEnd?: () => void) {
  if (!('speechSynthesis' in window) || ttsMuted.value || !text.trim()) {
    onEnd?.();
    return;
  }
  const u = new SpeechSynthesisUtterance(text);
  if (voice) u.voice = voice;
  u.rate = 1.04;
  u.pitch = 1.0;
  utteranceQueue++;
  speaking.value = true;
  const finish = () => {
    utteranceQueue = Math.max(0, utteranceQueue - 1);
    if (utteranceQueue === 0) {
      speaking.value = false;
      onEnd?.();
    }
  };
  u.onend = finish;
  u.onerror = finish;
  speechSynthesis.speak(u);
}

export function stopSpeaking() {
  speechSynthesis.cancel();
  utteranceQueue = 0;
  speaking.value = false;
}
